import Phaser from 'phaser';
import { NetworkManager } from '../../../managers/NetworkManager';
import {
  ClientGameState,
  Card as CardType,
  Suit,
  PlayerPosition,
  CardPlayedMessage,
  TrickWonMessage,
  TrumpSelectedMessage,
  HandCompleteMessage,
  GameOverMessage,
  HakemCardRevealedMessage,
  HakemSelectedMessage,
  PartnerSelectedMessage,
  SeatsSwappedMessage,
  CardDealtMessage,
  StockDrawMessage,
  DiscardRequiredMessage,
  CardsDiscardedMessage,
  PlayerDisconnectedMessage,
  PlayerReplacedWithAIMessage,
  KickedMessage,
  AnimationEvent,
  AnimationSequenceId,
} from '@hokm/shared';

export interface NetworkEventCallbacks {
  // State access
  getGameState: () => ClientGameState | null;
  getMyPosition: () => PlayerPosition;
  toDisplayPosition: (serverPosition: PlayerPosition) => PlayerPosition;

  // State flags
  isCollectingTrick: () => boolean;
  getLastCollectedTrickNumber: () => number;
  getPendingCardPlay: () => CardType | null;

  // State updates
  setGameState: (state: ClientGameState) => void;
  clearPendingCardPlay: () => void;
  resetCollectionState: () => void;

  // Deduplication tracking
  hasProcessedCardPlay: (key: string) => boolean;
  markCardPlayProcessed: (key: string) => void;
  hasProcessedHakemReveal: (key: string) => boolean;
  markHakemRevealProcessed: (key: string) => void;
  clearProcessedCardPlays: () => void;
  clearProcessedHakemReveals: () => void;

  // Rendering
  renderGameState: (state: ClientGameState) => void;

  // Trick animations
  animateCardToTrick: (displayPosition: PlayerPosition, card: CardType) => void;
  animateTrickCollection: (winnerPosition: PlayerPosition, sequenceId?: AnimationSequenceId) => void;
  clearTrickCards: () => void;
  destroyHandCards: () => void;

  // Scoreboard updates
  updateScoreboardForTrickWon: (
    winnerPosition: PlayerPosition,
    isTeamMode: boolean,
    isLocalTeamA: boolean,
    handScore: { teamA: number; teamB: number } | Record<string, number>,
    winnerId?: string
  ) => void;
  setTrumpSuit: (suit: Suit, animate: boolean) => void;

  // Hand completion
  showHandResult: (data: HandCompleteMessage) => void;
  clearTableForNewHand: (sequenceId: AnimationSequenceId) => void;

  // Game over
  handleGameOver: (data: GameOverMessage) => void;

  // Ceremony animations
  animateHakemCardReveal: (data: HakemCardRevealedMessage) => void;
  queueAnnouncement: (fn: () => Promise<void>) => void;
  showHakemSelected: (data: HakemSelectedMessage) => Promise<void>;
  showPartnerSelected: (data: PartnerSelectedMessage) => Promise<void>;
  showSeatsSwapped: (data: SeatsSwappedMessage) => Promise<void>;

  // Dealing animations
  animateCardDealt: (data: CardDealtMessage) => void;
  animateStockDraw: (data: StockDrawMessage) => void;

  // Discard handling
  showDiscardUI: (data: DiscardRequiredMessage) => void;
  clearDiscardMode: () => void;

  // Reconnection
  clearReconnectionState: () => void;
  clearAnnouncementQueue: () => void;
  clearDealtTracking: () => void;

  // Disconnect notifications
  showDisconnectNotification: (playerId: string, playerName: string, position: PlayerPosition) => void;
  hideDisconnectNotification: (playerId: string) => void;

  // Kicked handling
  handleKicked: (reason: string) => void;

  // Animation ack
  sendAnimationAck: (event: AnimationEvent, sequenceId: AnimationSequenceId) => void;
}

/**
 * Handles all network event setup and cleanup for GameScene.
 */
export class NetworkEventHandler {
  private scene: Phaser.Scene;
  private networkManager: NetworkManager;
  private callbacks: NetworkEventCallbacks;

  // Store bound handlers for cleanup
  private handleGameStateBound: (state: ClientGameState) => void;
  private handleCardPlayedBound: (data: CardPlayedMessage) => void;
  private handleTrickWonBound: (data: TrickWonMessage) => void;
  private handleTrumpSelectedBound: (data: TrumpSelectedMessage) => void;
  private handleHandCompleteBound: (data: HandCompleteMessage) => void;
  private handleGameOverBound: (data: GameOverMessage) => void;
  private handleHakemCardRevealedBound: (data: HakemCardRevealedMessage) => void;
  private handleHakemSelectedBound: (data: HakemSelectedMessage) => void;
  private handlePartnerSelectedBound: (data: PartnerSelectedMessage) => void;
  private handleSeatsSwappedBound: (data: SeatsSwappedMessage) => void;
  private handleCardDealtBound: (data: CardDealtMessage) => void;
  private handleStockDrawBound: (data: StockDrawMessage) => void;
  private handleDiscardRequiredBound: (data: DiscardRequiredMessage) => void;
  private handleCardsDiscardedBound: (data: CardsDiscardedMessage) => void;
  private handleReconnectingBound: () => void;
  private handleReconnectedBound: () => void;
  private handlePlayerDisconnectedBound: (data: PlayerDisconnectedMessage) => void;
  private handlePlayerReplacedWithAIBound: (data: PlayerReplacedWithAIMessage) => void;
  private handleKickedBound: (data: KickedMessage) => void;
  private handlePlayerRemovedBound: (data: { playerId: string; position: PlayerPosition }) => void;

  constructor(scene: Phaser.Scene, networkManager: NetworkManager, callbacks: NetworkEventCallbacks) {
    this.scene = scene;
    this.networkManager = networkManager;
    this.callbacks = callbacks;

    // Bind all handlers
    this.handleGameStateBound = this.handleGameState.bind(this);
    this.handleCardPlayedBound = this.handleCardPlayed.bind(this);
    this.handleTrickWonBound = this.handleTrickWon.bind(this);
    this.handleTrumpSelectedBound = this.handleTrumpSelected.bind(this);
    this.handleHandCompleteBound = this.handleHandComplete.bind(this);
    this.handleGameOverBound = this.handleGameOver.bind(this);
    this.handleHakemCardRevealedBound = this.handleHakemCardRevealed.bind(this);
    this.handleHakemSelectedBound = this.handleHakemSelected.bind(this);
    this.handlePartnerSelectedBound = this.handlePartnerSelected.bind(this);
    this.handleSeatsSwappedBound = this.handleSeatsSwapped.bind(this);
    this.handleCardDealtBound = this.handleCardDealt.bind(this);
    this.handleStockDrawBound = this.handleStockDraw.bind(this);
    this.handleDiscardRequiredBound = this.handleDiscardRequired.bind(this);
    this.handleCardsDiscardedBound = this.handleCardsDiscarded.bind(this);
    this.handleReconnectingBound = this.handleReconnecting.bind(this);
    this.handleReconnectedBound = this.handleReconnected.bind(this);
    this.handlePlayerDisconnectedBound = this.handlePlayerDisconnected.bind(this);
    this.handlePlayerReplacedWithAIBound = this.handlePlayerReplacedWithAI.bind(this);
    this.handleKickedBound = this.handleKicked.bind(this);
    this.handlePlayerRemovedBound = this.handlePlayerRemoved.bind(this);

    this.setupListeners();
  }

  private setupListeners(): void {
    this.networkManager.on('gameState', this.handleGameStateBound);
    this.networkManager.on('cardPlayed', this.handleCardPlayedBound);
    this.networkManager.on('trickWon', this.handleTrickWonBound);
    this.networkManager.on('trumpSelected', this.handleTrumpSelectedBound);
    this.networkManager.on('handComplete', this.handleHandCompleteBound);
    this.networkManager.on('gameOver', this.handleGameOverBound);
    this.networkManager.on('hakemCardRevealed', this.handleHakemCardRevealedBound);
    this.networkManager.on('hakemSelected', this.handleHakemSelectedBound);
    this.networkManager.on('partnerSelected', this.handlePartnerSelectedBound);
    this.networkManager.on('seatsSwapped', this.handleSeatsSwappedBound);
    this.networkManager.on('cardDealt', this.handleCardDealtBound);
    this.networkManager.on('stockDraw', this.handleStockDrawBound);
    this.networkManager.on('discardRequired', this.handleDiscardRequiredBound);
    this.networkManager.on('cardsDiscarded', this.handleCardsDiscardedBound);
    this.networkManager.on('reconnecting', this.handleReconnectingBound);
    this.networkManager.on('reconnected', this.handleReconnectedBound);
    this.networkManager.on('playerDisconnected', this.handlePlayerDisconnectedBound);
    this.networkManager.on('playerReplacedWithAI', this.handlePlayerReplacedWithAIBound);
    this.networkManager.on('kicked', this.handleKickedBound);
    this.networkManager.on('playerRemoved', this.handlePlayerRemovedBound);
  }

  private handleGameState(state: ClientGameState): void {
    // Defensive reset: if we receive a game state for a new trick,
    // ensure we're not stuck in collecting state from a failed animation
    if (this.callbacks.isCollectingTrick() && state.trickNumber > this.callbacks.getLastCollectedTrickNumber()) {
      console.warn('[GameState] Resetting stuck isCollectingTrick flag');
      this.callbacks.resetCollectionState();
      this.callbacks.clearTrickCards();
      this.callbacks.clearProcessedCardPlays();
    }

    this.callbacks.setGameState(state);
    this.callbacks.renderGameState(state);
  }

  private handleCardPlayed(data: CardPlayedMessage): void {
    // Generate unique key including trick number to avoid cross-trick deduplication
    const trickNum = this.callbacks.getGameState()?.trickNumber ?? 0;
    const cardKey = `${trickNum}-${data.position}-${data.card.suit}-${data.card.rank}`;

    // Skip duplicate events - server sends multiple times
    if (this.callbacks.hasProcessedCardPlay(cardKey)) {
      return;
    }
    this.callbacks.markCardPlayProcessed(cardKey);

    // Clear pendingCardPlay if server confirms our card was played
    const myPosition = this.callbacks.getMyPosition();
    if (data.position === myPosition) {
      const pending = this.callbacks.getPendingCardPlay();
      if (pending && pending.suit === data.card.suit && pending.rank === data.card.rank) {
        this.callbacks.clearPendingCardPlay();
      }
      // Skip animation for local player - already done optimistically
      return;
    }

    // Skip if collection is in progress - renderTrick will handle after collection
    if (this.callbacks.isCollectingTrick()) {
      return;
    }

    // Convert server position to display position
    const displayPosition = this.callbacks.toDisplayPosition(data.position);
    this.callbacks.animateCardToTrick(displayPosition, data.card);
  }

  private handleTrickWon(data: TrickWonMessage): void {
    // Skip if already collecting (duplicate event)
    if (this.callbacks.isCollectingTrick()) {
      return;
    }

    const state = this.callbacks.getGameState();

    // Update scoreboard for trick won (TrickAnimator handles lastCollectedTrickNumber internally)
    if (state) {
      const isTeamMode = state.players.length === 4;
      const isLocalTeamA = state.myPosition % 2 === 0;
      const winnerIsTeamA = data.winnerPosition % 2 === 0;

      if (isTeamMode) {
        const handScore = state.handScore as { teamA: number; teamB: number };
        if (winnerIsTeamA) {
          handScore.teamA++;
        } else {
          handScore.teamB++;
        }
        this.callbacks.updateScoreboardForTrickWon(data.winnerPosition, true, isLocalTeamA, handScore);
      } else {
        const winner = state.players[data.winnerPosition];
        if (winner) {
          const handScore = state.handScore as Record<string, number>;
          handScore[winner.id] = (handScore[winner.id] || 0) + 1;
          this.callbacks.updateScoreboardForTrickWon(data.winnerPosition, false, isLocalTeamA, handScore, winner.id);
        }
      }
    }

    const displayPosition = this.callbacks.toDisplayPosition(data.winnerPosition);
    this.callbacks.animateTrickCollection(displayPosition, data.sequenceId);
  }

  private handleTrumpSelected(data: TrumpSelectedMessage): void {
    // Animate trump in scoreboard
    this.callbacks.setTrumpSuit(data.suit, true);
    // Send acknowledgment after trump animation completes (400ms)
    this.scene.time.delayedCall(400, () => {
      this.callbacks.sendAnimationAck(AnimationEvent.TRUMP_SELECTED, data.sequenceId);
    });
  }

  private handleHandComplete(data: HandCompleteMessage): void {
    this.callbacks.clearProcessedCardPlays(); // Also resets lastCollectedTrickNumber via TrickAnimator.reset()
    this.callbacks.clearProcessedHakemReveals();
    this.callbacks.showHandResult(data);

    // After showing hand result, clear table for next hand
    this.scene.time.delayedCall(2500, () => {
      this.callbacks.clearTableForNewHand(data.sequenceId);
    });
  }

  private handleGameOver(data: GameOverMessage): void {
    this.callbacks.handleGameOver(data);
  }

  private handleHakemCardRevealed(data: HakemCardRevealedMessage): void {
    // Deduplicate by unique key (server may send multiple times)
    const revealKey = `${data.recipientPosition}-${data.card.suit}-${data.card.rank}`;
    if (this.callbacks.hasProcessedHakemReveal(revealKey)) {
      return;
    }
    this.callbacks.markHakemRevealProcessed(revealKey);
    this.callbacks.animateHakemCardReveal(data);
  }

  private handleHakemSelected(data: HakemSelectedMessage): void {
    this.callbacks.queueAnnouncement(() => this.callbacks.showHakemSelected(data));
  }

  private handlePartnerSelected(data: PartnerSelectedMessage): void {
    this.callbacks.queueAnnouncement(() => this.callbacks.showPartnerSelected(data));
  }

  private handleSeatsSwapped(data: SeatsSwappedMessage): void {
    this.callbacks.queueAnnouncement(() => this.callbacks.showSeatsSwapped(data));
  }

  private handleCardDealt(data: CardDealtMessage): void {
    this.callbacks.animateCardDealt(data);
  }

  private handleStockDraw(data: StockDrawMessage): void {
    this.callbacks.animateStockDraw(data);
  }

  private handleDiscardRequired(data: DiscardRequiredMessage): void {
    this.callbacks.showDiscardUI(data);
  }

  private handleCardsDiscarded(_data: CardsDiscardedMessage): void {
    // Server confirmed discard - clean up discard UI
    this.callbacks.clearDiscardMode();
  }

  private handleReconnecting(): void {
    // Clear pending states that may be stale after reconnection
    this.callbacks.clearReconnectionState();
    this.callbacks.clearProcessedCardPlays();
    this.callbacks.clearProcessedHakemReveals();
    this.callbacks.clearDiscardMode();
    this.callbacks.clearAnnouncementQueue();
    this.callbacks.clearDealtTracking();
    this.callbacks.clearTrickCards();
    this.callbacks.destroyHandCards();
  }

  private handleReconnected(): void {
    // Server will send fresh gameState, so we're ready
    console.log('Reconnected to server');
  }

  private handlePlayerDisconnected(data: PlayerDisconnectedMessage): void {
    this.callbacks.showDisconnectNotification(data.playerId, data.playerName, data.position);
  }

  private handlePlayerReplacedWithAI(data: PlayerReplacedWithAIMessage): void {
    this.callbacks.hideDisconnectNotification(data.playerId);
  }

  private handleKicked(data: KickedMessage): void {
    this.callbacks.handleKicked(data.reason);
  }

  private handlePlayerRemoved(data: { playerId: string; position: PlayerPosition }): void {
    this.callbacks.hideDisconnectNotification(data.playerId);
    // Game state will be updated via gameState event, which will re-render
  }

  /**
   * Clean up all event listeners.
   */
  destroy(): void {
    this.networkManager.off('gameState', this.handleGameStateBound);
    this.networkManager.off('cardPlayed', this.handleCardPlayedBound);
    this.networkManager.off('trickWon', this.handleTrickWonBound);
    this.networkManager.off('trumpSelected', this.handleTrumpSelectedBound);
    this.networkManager.off('handComplete', this.handleHandCompleteBound);
    this.networkManager.off('gameOver', this.handleGameOverBound);
    this.networkManager.off('hakemCardRevealed', this.handleHakemCardRevealedBound);
    this.networkManager.off('hakemSelected', this.handleHakemSelectedBound);
    this.networkManager.off('partnerSelected', this.handlePartnerSelectedBound);
    this.networkManager.off('seatsSwapped', this.handleSeatsSwappedBound);
    this.networkManager.off('cardDealt', this.handleCardDealtBound);
    this.networkManager.off('stockDraw', this.handleStockDrawBound);
    this.networkManager.off('discardRequired', this.handleDiscardRequiredBound);
    this.networkManager.off('cardsDiscarded', this.handleCardsDiscardedBound);
    this.networkManager.off('reconnecting', this.handleReconnectingBound);
    this.networkManager.off('reconnected', this.handleReconnectedBound);
    this.networkManager.off('playerDisconnected', this.handlePlayerDisconnectedBound);
    this.networkManager.off('playerReplacedWithAI', this.handlePlayerReplacedWithAIBound);
    this.networkManager.off('kicked', this.handleKickedBound);
    this.networkManager.off('playerRemoved', this.handlePlayerRemovedBound);
  }
}
